import React, { useMemo, useState } from "react";
import { Money } from "../../../components";
import { Icon, IconButton } from "../../../components/ui";
import RemovePriceConfirm from "./RemovePriceConfirm";
import axios from "axios";
import { monthLabels, boxLabels } from "../../constants";
import { sourceOptions } from "../../constants";
import CollapseButton from "./CollapseButton";
import { conditionLabels } from "../../constants";
import dayjs from "dayjs";
import AvailablePriceItem from "./AvailablePriceItem";
import useTradeImage from "./useTradeImage";

const ucFirst = (string) => {
  return string ? string[0].toUpperCase() + string.substring(1) : string;
};

const convertString = (string) => {
  let arr;
  try {
    arr = JSON.parse(string);
  } catch (_) {
    return undefined; // missing/blank/non-JSON — render nothing
  }
  if (arr && arr.length > 0) {
    return arr.map((item) => conditionLabels[item] || item).join(", ");
  }
};

const AvailablePriceItemRenderer = ({
  item,
  onItemRemoved,
  setModalOpen,
  setModalItem,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const tradeImage = useTradeImage(item);
  const year = item?.year_month?.substr(0, 4);
  const month = item?.year_month?.substr(5, 2);
  const monthLabel = monthLabels?.[month * 1];
  const locationName = item?.location?.replace("-e", " E").replace("uk", "UK");

  const getSourceName = (source) => {
    return sourceOptions.find((option) => option.id === source)?.name;
  };

  const removeItem = async (id) => {
    try {
      await axios.delete(`/api/product-prices/${id}/delete`);
      onItemRemoved();
    } catch (error) {
      // Reported to the console rather than swallowed. The confirm modal is
      // driven by `isRemoving`, so closing it moved to `finally`: a failed
      // delete used to leave it open with no message and no way to tell.
      console.error("Failed to remove price", error);
    } finally {
      setIsRemoving(false);
    }
  };

  const price = useMemo(() => {
    if (item?.additional_records?.length > 0) {
      if (isOpen) {
        return parseInt(item?.gbp_price / 100) * 100;
      } else {
        const total = item?.additional_records?.reduce((acc, cur) => {
          return acc + parseInt(cur?.gbp_price) * 1;
        }, parseInt(item?.gbp_price) * 1);

        const count = (item?.additional_records?.length || 0) + 1;
        return parseInt(total / count / 100) * 100;
      }
    }

    return parseInt(item?.gbp_price / 100) * 100;
  }, [item, isOpen]);

  const collapsedUnderprice = useMemo(() => {
    return (
      !isOpen &&
      (item?.additional_records?.some((record) => record.under_priced) ?? false)
    );
  }, [item, isOpen]);

  const oldestDateFormatted = useMemo(() => {
    if (!item?.additional_records?.length) {
      return "Today";
    }

    const allDates = [
      item.price_date,
      ...item.additional_records.map((record) => record.price_date),
    ].filter((date) => date);

    if (allDates.length === 0) {
      return "Today";
    }

    const oldestDate = allDates.reduce((oldest, current) => {
      return dayjs(current).isBefore(dayjs(oldest)) ? current : oldest;
    });

    const daysDiff = dayjs().diff(dayjs(oldestDate), "day");

    if (daysDiff === 0) {
      return "Today";
    } else if (daysDiff === 1) {
      return "1 day ago";
    } else {
      return `${daysDiff} days ago`;
    }
  }, [item]);

  // The link used to be href="javascript::void(0)" — the double colon is a
  // JS syntax error, so every click threw "SyntaxError: Unexpected token ':'"
  // at the page URL. It is a real <button> now (IconButton), so nothing navigates.
  const handleOpen = (e) => {
    e?.preventDefault?.();
    setModalItem({
      product_id: item.product_id,
      message_id: item.listing_code,
      messageStatus: "approved",
    });
    setModalOpen(true);
  };

  return (
    <>
      <tr
        key={item.id}
        className="price-list"
        style={{
          backgroundColor: `${
            item.under_priced ? "#A4E6A0" : collapsedUnderprice ? "#A4E6A0" : ""
          }`,
        }}
      >
        <td style={{ width: 20 }}>
          {item?.additional_records?.length > 0 && (
            <CollapseButton isOpen={isOpen} setIsOpen={setIsOpen} />
          )}
        </td>
        <td className="price-list__thumb">
          {tradeImage.src ? (
            <img src={tradeImage.src} alt="" loading="lazy" />
          ) : tradeImage.showPlaceholder ? (
            <span className="price-list__no-photo">No photo</span>
          ) : null}
        </td>
        <td>
          {oldestDateFormatted}
          &nbsp;
        </td>
        <td>{getSourceName(item?.source)}&nbsp;</td>
        <td>
          {monthLabel ? `${monthLabel} ` : ""}
          {year}&nbsp;
        </td>
        <td>{convertString(item?.condition)}&nbsp;</td>
        <td>
          <IconButton
            variant="ghost"
            size="sm"
            label="Open price source"
            data-btn="price-open"
            onClick={handleOpen}
          >
            <Icon name="ExternalLink" size="sm" />
          </IconButton>
        </td>
        <td>
          {/* price is the gbp_price pivot — explicit GBP. */}
          <Money amount={price} currency="gbp" style={{ fontSize: "14px" }} />
        </td>
        <td>{boxLabels?.[item?.box]}&nbsp;</td>
        <td>
          {item.supplier_id ? item.supplier_name : item.supplier}
          &nbsp;
        </td>
        <td>{item.store_name}&nbsp;</td>
        <td>{ucFirst(locationName)}&nbsp;</td>
        <td>
          {isOpen === false && item?.additional_records > 0 ? (
            <>&nbsp;</>
          ) : (
            <IconButton
              variant="ghost"
              size="sm"
              label="Remove price"
              data-btn="price-delete"
              onClick={() => setIsRemoving(true)}
            >
              <Icon name="Trash2" size="sm" />
            </IconButton>
          )}
        </td>
      </tr>

      {isOpen &&
        item?.additional_records?.map((record, index) => (
          <AvailablePriceItem
            item={record}
            key={index}
            removeItem={removeItem}
            isRemoving={isRemoving}
            setIsRemoving={setIsRemoving}
          />
        ))}

      <RemovePriceConfirm
        isOpen={isRemoving}
        onCancel={() => setIsRemoving(false)}
        onRemove={() => removeItem(item.id)}
      />
    </>
  );
};

export default AvailablePriceItemRenderer;
